import React, { useState, useEffect } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { TierBadge } from '@/components/common/TierBadge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Filter } from 'lucide-react';
import API from '@/api/client';
import { formatNumber, formatCurrency } from '@/utils/helpers';
import { toast } from 'sonner';

const AdminTherapists = () => {
  const [loading, setLoading] = useState(true);
  const [therapists, setTherapists] = useState([]);
  const [filters, setFilters] = useState({
    tier: '',
    region: '',
    search: '',
  });

  useEffect(() => {
    fetchTherapists();
  }, [filters.tier, filters.region]);

  const fetchTherapists = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.tier) params.append('tier', filters.tier);
      if (filters.region) params.append('region', filters.region);
      
      const response = await API.get(`/admin/therapists?${params.toString()}`);
      setTherapists(response.data.therapists);
    } catch (error) {
      console.error('Error fetching therapists:', error);
      toast.error('Failed to load therapists');
    } finally {
      setLoading(false);
    }
  };

  const filteredTherapists = therapists.filter(t => {
    if (!filters.search) return true;
    const searchLower = filters.search.toLowerCase();
    return (
      t.user?.full_name?.toLowerCase().includes(searchLower) ||
      t.user?.email?.toLowerCase().includes(searchLower) ||
      t.specialization?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50 px-4 md:px-6 py-4">
        <div className="flex items-center gap-4">
          <img
            src="https://customer-assets.emergentagent.com/job_e65b2a0c-b7a8-46e9-8a92-d5df4207b5ae/artifacts/8vn39jol_Physiomobile%20Logo.jpeg"
            alt="Physiomobile Logo"
            className="h-10"
          />
          <h1 className="font-heading text-2xl font-bold text-gray-800">Flywheel HQ</h1>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-8 py-8">
        <PageHeader
          title="Therapist Management"
          subtitle="Monitor and manage therapist performance"
          actions={
            <Button onClick={() => window.location.href = '/admin/dashboard'}>
              Back to Dashboard
            </Button>
          }
        />

        {/* Filters */}
        <Card className="p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name or email..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
                data-testid="therapist-search-input"
              />
            </div>

            <Select value={filters.tier} onValueChange={(value) => setFilters({ ...filters, tier: value })}>
              <SelectTrigger data-testid="tier-filter">
                <SelectValue placeholder="All Tiers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="bronze">Bronze</SelectItem>
                <SelectItem value="silver">Silver</SelectItem>
                <SelectItem value="gold">Gold</SelectItem>
                <SelectItem value="elite">Elite</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.region} onValueChange={(value) => setFilters({ ...filters, region: value })}>
              <SelectTrigger data-testid="region-filter">
                <SelectValue placeholder="All Regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                <SelectItem value="Klang Valley">Klang Valley</SelectItem>
                <SelectItem value="Johor">Johor</SelectItem>
                <SelectItem value="Selangor">Selangor</SelectItem>
                <SelectItem value="Penang">Penang</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={fetchTherapists} variant="outline" className="w-full">
              <Filter className="w-4 h-4 mr-2" />
              Apply Filters
            </Button>
          </div>
        </Card>

        {/* Therapists List */}
        {loading ? (
          <LoadingSpinner size="lg" text="Loading therapists..." />
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground mb-4">
              Showing {filteredTherapists.length} therapists
            </div>
            {filteredTherapists.map((therapist, index) => (
              <Card
                key={therapist.id}
                className="p-6 hover:shadow-md transition-shadow"
                data-testid={`therapist-card-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading text-xl font-semibold text-gray-800">
                        {therapist.user?.full_name || 'N/A'}
                      </h3>
                      <TierBadge tier={therapist.tier} />
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      {therapist.user?.email} • {therapist.license_number}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Specialization</p>
                        <p className="font-medium">{therapist.specialization}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Region</p>
                        <p className="font-medium">{therapist.region}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Sessions</p>
                        <p className="font-medium">{formatNumber(therapist.total_sessions)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Rating</p>
                        <p className="font-medium">{therapist.average_rating?.toFixed(1)} / 5.0</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Acceptance</p>
                        <p className="font-medium">{(therapist.acceptance_rate * 100).toFixed(0)}%</p>
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    View Details
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminTherapists;